import type { FC } from "react";
import { uppercaseFirstCharacter } from "@lib/utils/commonUtility";
import type { BreadcrumbProps } from "@ecco/ui/breadcrumb";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink } from "@ecco/ui/breadcrumb";
import { CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { PDPSeoBreadcrumbSchema } from "@components/seo/PDPSeoBreadcrumbSchema";
import { BREADCRUMBS_ITEM, BREADCRUMBS_WRAPPER } from "@e2e/helpers/common/constants/common";
import { Text } from "@ecco/ui/text";

interface IBreadcrumbsProps extends BreadcrumbProps {
    items: { label: string; link: string }[];
    renderLastAsText?: boolean;
    isPDP?: boolean;
}

export const Breadcrumbs: FC<IBreadcrumbsProps> = ({
    items,
    renderLastAsText = false,
    ...rest
}) => {
    const { get: getMicrocopy } = useMicrocopy();

    if (!items?.length) return null;

    return (
        <>
            <PDPSeoBreadcrumbSchema breadcrumbsData={items} />
            <Breadcrumb
                aria-label={
                    getMicrocopy(CommonCMS.shared, CommonCMS.ariaLabels.breadcrumbAria) ||
                    "Breadcrumbs"
                }
                data-testid={BREADCRUMBS_WRAPPER}
                {...rest}
            >
                {items.map((item, index) => {
                    const isLastItem = index === items.length - 1;
                    const itemLabel = uppercaseFirstCharacter(item.label);

                    return (
                        <BreadcrumbItem
                            key={item.label + index}
                            color={"black"}
                            data-testid={BREADCRUMBS_ITEM}
                        >
                            {isLastItem && renderLastAsText ? (
                                <Text as="span" fontWeight="bold" fontSize={"mobileLinkL1"}>
                                    {itemLabel}
                                </Text>
                            ) : (
                                <BreadcrumbLink
                                    href={item.link}
                                    fontSize={["mobileLinkL1", "desktopLinkL1"]}
                                    aria-current={isLastItem ? "location" : undefined}
                                >
                                    {itemLabel}
                                </BreadcrumbLink>
                            )}
                        </BreadcrumbItem>
                    );
                })}
            </Breadcrumb>
        </>
    );
};
